import { useState, useMemo, useCallback } from "react";
import type { BlockConfig } from "@/lib/registry/types";
// Import the specific sort option type
import type { SortOptionValue } from "./filter-controls";
// Remove unused Item type import
// import type { Item } from "./component-card"; // Import the Item type

// Reuse the Item type definition from other files
// Removed local Item type definition
// type Item = BlockConfig & {
//     id: string;
//     library?: string;
//     name: string;
//     description?: string;
//     categories?: string[];
//     tags?: string[];
//     previewProps?: {
//         previewPath: string;
//         hasPreviewImage: boolean;
//         availablePreviewFiles: string[];
//         demoFiles?: string[];
//     };
// };

interface UseComponentFiltersProps {
	initialItems: (BlockConfig & { id: string })[]; // Use BlockConfig directly
	initialFilter?: {
		libraries: string[];
		categories: string[];
	};
	starredItemsSet: Set<string>; // Receive starred set from useStarredItems
}

export function useComponentFilters({
	initialItems,
	initialFilter,
	starredItemsSet,
}: UseComponentFiltersProps) {
	const [searchQuery, setSearchQuery] = useState("");
	const [activeLibraries, setActiveLibraries] = useState<string[]>(
		initialFilter?.libraries || []
	);
	const [activeCategories, setActiveCategories] = useState<string[]>(
		initialFilter?.categories || []
	);
	// Use the specific type for sortOption, default to 'name-asc'
	const [sortOption, setSortOption] = useState<SortOptionValue>("name-asc");
	const [showStarredOnly, setShowStarredOnly] = useState(false);

	// All unique libraries and categories derived from initial items
	const allLibraries = useMemo(
		() =>
			[...new Set(initialItems.map((item) => item.library as string))]
				.filter(Boolean) // Filter out potential undefined/null if library is optional
				.sort(),
		[initialItems]
	);
	const allCategories = useMemo(
		() =>
			[
				...new Set(
					initialItems.flatMap((item) => item.categories || []).filter(Boolean)
				),
			].sort(),
		[initialItems]
	);

	// Filter and sort items based on current state
	const filteredAndSortedItems = useMemo(() => {
		let filtered = showStarredOnly
			? initialItems.filter((item) => starredItemsSet.has(item.id))
			: [...initialItems]; // Create a copy to avoid modifying the original array

		// Library filtering
		if (activeLibraries.length > 0) {
			filtered = filtered.filter(
				(item) => item.library && activeLibraries.includes(item.library)
			);
		}

		// Category filtering
		if (activeCategories.length > 0) {
			filtered = filtered.filter((item) =>
				activeCategories.some(
					(cat) => item.categories && item.categories.includes(cat)
				)
			);
		}

		// Search filtering (case-insensitive)
		const query = searchQuery.toLowerCase().trim();
		if (query) {
			filtered = filtered.filter(
				(item) =>
					item.name.toLowerCase().includes(query) ||
					(item.description && item.description.toLowerCase().includes(query)) ||
					(item.library && item.library.toLowerCase().includes(query)) ||
					item.tags?.some((tag) => tag.toLowerCase().includes(query))
			);
		}

		// Sorting
		switch (sortOption) {
			case "name-asc":
				filtered.sort((a, b) => a.name.localeCompare(b.name));
				break;
			case "name-desc":
				filtered.sort((a, b) => b.name.localeCompare(a.name));
				break;
			// Add other sorting options (e.g., by library)
			case "library-asc":
				filtered.sort((a, b) => (a.library ?? "").localeCompare(b.library ?? ""));
				break;
			// Add default case or handle other options if necessary
		}

		return filtered;
	}, [
		initialItems,
		activeLibraries,
		activeCategories,
		searchQuery,
		sortOption,
		showStarredOnly,
		starredItemsSet, // Ensure hook updates when starred items change
	]);

	// --- Filter Handlers (using useCallback for stability) ---
	const handleLibraryToggle = useCallback((library: string) => {
		setActiveLibraries((prev) => {
			const next = new Set(prev);
			next.has(library) ? next.delete(library) : next.add(library);
			return Array.from(next);
		});
	}, []);

	const handleCategoryToggle = useCallback((category: string) => {
		setActiveCategories((prev) => {
			const next = new Set(prev);
			next.has(category) ? next.delete(category) : next.add(category);
			return Array.from(next);
		});
	}, []);

	const handleShowOnlyLibrary = useCallback((library: string) => {
		setActiveLibraries([library]);
		setActiveCategories([]); // Reset other filter type for exclusivity
	}, []);

	const handleShowOnlyCategory = useCallback((category: string) => {
		setActiveCategories([category]);
		setActiveLibraries([]); // Reset other filter type for exclusivity
	}, []);

	const handleResetLibraries = useCallback(() => setActiveLibraries([]), []);
	const handleResetCategories = useCallback(() => setActiveCategories([]), []);

	const handleResetAllFilters = useCallback(() => {
		setActiveLibraries([]);
		setActiveCategories([]);
		setSearchQuery("");
		setShowStarredOnly(false);
		setSortOption("name-asc"); // Reset sort to default
	}, []);

	// --- Search Handler ---
	const handleSearchChange = useCallback((value: string) => {
		setSearchQuery(value);
	}, []);

	// --- Starred Only Handler ---
	const handleToggleStarredOnly = useCallback((checked: boolean) => {
		setShowStarredOnly(checked);
	}, []);

	// --- Sort Handler --- Use specific type
	const handleSortChange = useCallback((value: SortOptionValue) => {
		setSortOption(value);
	}, []);

	return {
		// Filter State
		searchQuery,
		activeLibraries,
		activeCategories,
		sortOption,
		showStarredOnly,
		// Derived Data
		allLibraries,
		allCategories,
		filteredAndSortedItems,
		// Handlers
		handleSearchChange,
		handleLibraryToggle,
		handleCategoryToggle,
		handleShowOnlyLibrary,
		handleShowOnlyCategory,
		handleResetLibraries,
		handleResetCategories,
		handleResetAllFilters,
		handleToggleStarredOnly,
		handleSortChange,
	};
}
